import { FieldSwitch } from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import { useCallback } from 'react';

import { CustomFieldAttribute, customFieldsApi } from 'apis/customFields';
import { useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

type DeactivateToggleProps = {
  attribute: CustomFieldAttribute;
  onChange?: (newState: boolean) => void;
};

/** Represents the a toggle to activate/deactivate a custom attribute. */
export const ActiveToggle = ({
  attribute,
  onChange,
}: DeactivateToggleProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();
  const {
    id: attributeId,
    entity_id: entityId,
    is_deactivated,
    type,
  } = attribute;
  const isDeactivating = !is_deactivated;

  /** Handles deleting custom attribute. */
  const handleToggle = useCallback(async () => {
    onChange?.(isDeactivating);

    const response = await dispatch(
      customFieldsApi.endpoints.updateAttribute.initiate({
        attributeId,
        entityId,
        patch: {
          before: attribute,
          after: {
            ...attribute,
            is_deactivated: isDeactivating,
          },
        },
      }),
    );

    if (!isResponseWithData(response)) {
      notify({
        title: isDeactivating
          ? 'Could not activate custom attribute'
          : 'Could not deactivate custom attribute',
        text: isDeactivating
          ? 'An error occurred while trying to activate a custom attribute'
          : 'An error occurred while trying to deactivate a custom attribute',
        type: 'error',
        icon: 'exclamation',
        status: 'danger',
      });
    }

    if (isDeactivating) {
      ampli.settingsCustomAttributeDeactivated({
        attributeId,
        entityId,
        type,
      });
    } else {
      ampli.settingsCustomAttributeActivated({
        attributeId,
        entityId,
        type,
      });
    }
  }, [
    attribute,
    attributeId,
    dispatch,
    entityId,
    isDeactivating,
    notify,
    onChange,
    type,
  ]);

  return (
    <FieldSwitch
      input={{
        name: 'active',
        value: 'active',
        checked: isDeactivating,
        onChange: handleToggle,
      }}
    />
  );
};
